import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Hero.module.css';
import { serviceDetails } from '../utils/serviceDetails';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredServices, setFilteredServices] = useState([]);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const slides = [
    {
      image: '/img/slider1.webp',
      title: 'Premium Financial Services',
      subtitle: 'Expert Guidance for Your Business Success',
    },
    {
      image: '/img/slider2.webp',
      title: 'Comprehensive Business Solutions',
      subtitle: 'From Registration to Compliance',
    },
    {
      image: '/img/slider3.webp',
      title: 'Trusted by Thousands',
      subtitle: 'Your Partner in Financial Excellence',
    },
    {
      image: '/img/slider4.webp',
      title: 'Professional Excellence',
      subtitle: 'Delivering Quality Services Since Day One',
    },
  ];

  // Get list of all available services for search
  const allServices = Object.keys(serviceDetails);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    // Click outside to close suggestions
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      clearInterval(interval);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 0) {
      const filtered = allServices.filter(service =>
        service.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredServices(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleServiceClick = (service) => {
    navigate(`/service/${encodeURIComponent(service)}`);
    setShowSuggestions(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (filteredServices.length > 0) {
      handleServiceClick(filteredServices[0]);
    }
  };

  return (
    <section className={styles.hero}>
      {/* Background Slider */}
      <div className={styles.heroSlider}>
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`${styles.heroSlide} ${index === currentSlide ? styles.active : ''}`}
            style={{
              backgroundImage: `url(${slide.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className={styles.heroOverlay}></div>
          </div>
        ))}
      </div>

      {/* Static Overlay Content */}
      <div className={styles.heroContent}>
        <div className="container">
          <div className={`${styles.heroText}`}>
            {/* Animated Title & Subtitle */}
            <div className={styles.textContent}>
              <h1 className={`${styles.heroTitle} key-${currentSlide}`}>{slides[currentSlide].title}</h1>
              <p className={`${styles.heroSubtitle} key-${currentSlide}`}>{slides[currentSlide].subtitle}</p>
            </div>

            {/* Static Search Bar */}
            <div className={styles.searchContainer} ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className={styles.searchForm}>
                <i className={`fas fa-search ${styles.searchIcon}`}></i>
                <input
                  type="text"
                  placeholder="Search services..."
                  className={styles.searchInput}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => searchQuery.length > 0 && setShowSuggestions(true)}
                />
                <button type="submit" className={styles.searchButton}>Search</button>
              </form>

              {showSuggestions && filteredServices.length > 0 && (
                <div className={styles.suggestionsList}>
                  {filteredServices.slice(0, 5).map((service, idx) => (
                    <div
                      key={idx}
                      className={styles.suggestionItem}
                      onClick={() => handleServiceClick(service)}
                    >
                      <i className="fas fa-arrow-right"></i>
                      {service}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Static CTA Buttons */}
            <div className={styles.heroCta}>
              <a
                href="#services"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={(e) => {
                  e.preventDefault();
                  const servicesSection = document.getElementById('services');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                <i className="fas fa-th-large"></i>
                All Services
              </a>
              <a href="tel:+919552319748" className={`${styles.btn} ${styles.btnOutline}`}>
                <i className="fas fa-phone"></i>
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Strip */}
      <div className={styles.trustStrip}>
        <div className={styles.trustItem}>
          <i className="fas fa-users"></i>
          <span>500+ Happy Clients</span>
        </div>
        <div className={styles.trustItem}>
          <i className="fas fa-file-contract"></i>
          <span>1000+ Filings Done</span>
        </div>
        <div className={styles.trustItem}>
          <i className="fas fa-star"></i>
          <span>4.9/5 Average Rating</span>
        </div>
        <div className={styles.trustItem}>
          <i className="fas fa-clock"></i>
          <span>24/7 Expert Support</span>
        </div>
      </div>

      <div className={styles.heroIndicators}>
        {slides.map((_, index) => (
          <button
            key={index}
            className={`${styles.indicator} ${index === currentSlide ? styles.active : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;

